import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Timeline } from "primereact/timeline";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ReportActionLogEntryResponse } from "../../../../client";
import { getReportActionLog } from "../../../../api/routes";
import { PantheonAvatarWithoutMenu } from "../../../../components/avatar/Avatar";
import { useUsers } from "../../data";
import { displayNameForUser } from "../../../../util";
import sharedStyles from "./shared.module.css";
import styles from "./SubmissionHistoryDetail.module.css";
import {
  ACTION_TYPE_ICONS,
  ACTION_TYPE_SEVERITY,
  ActionType,
  formatLabel,
  formatMetadata,
  formatTimestamp,
} from "./submissionHistoryUtils";

const TIMELINE_MARKER_COLORS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "#2196F3",
  REPORT_CREATED: "#4CAF50",
  EMAIL_ADDED_TO_REPORT: "#2196F3",
  EMAIL_REJECTED_INVALID_SENDER: "#f44336",
  EMAIL_REJECTED_DUPLICATE: "#FF9800",
  EMAIL_REJECTED_NO_ATTACHMENTS: "#FF9800",
  EMAIL_REJECTED_REPLY_THREAD: "#FF9800",
  UPDATE_REPORT: "#009688",
  UPDATE_ADDRESSES: "#009688",
  INTEGRATION_JOB_ENQUEUED: "#9E9E9E",
  INTEGRATION_JOB_STARTED: "#FF9800",
  INTEGRATION_JOB_COMPLETED: "#4CAF50",
  INTEGRATION_JOB_FAILED: "#f44336",
  CLEARANCE_EMAIL_SENT: "#4CAF50",
  QUOTE_INDICATION_DRAFTED: "#3F51B5",
  ATTACHMENT_UPLOADED: "#607D8B",
  COMMENT_ADDED: "#673AB7",
  COMMENT_EDITED: "#673AB7",
  COMMENT_DELETED: "#FF9800",
  BOR_CREATED: "#FF9800",
  BOR_FINALIZED: "#4CAF50",
  BOR_RESCINDED: "#2196F3",
};

export const SubmissionHistoryDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const users = useUsers();

  const { data: detail, isLoading } = useQuery({
    queryKey: ["submission-history-detail", reportId],
    queryFn: () => getReportActionLog(reportId ?? ""),
    enabled: !!reportId,
  });

  const actions = detail?.actions ?? [];
  const companyName = detail?.company_name;
  const emailSubject = detail?.email_subject;

  const findUserName = (userId: string | null): string | undefined => {
    if (!userId) return undefined;
    const user = (users?.data?.all_company_users ?? []).find(
      (u) => u.id === userId
    );
    return displayNameForUser(user) ?? userId;
  };

  const markerTemplate = (item: ReportActionLogEntryResponse) => {
    const color = TIMELINE_MARKER_COLORS[item.action_type] ?? "#9E9E9E";
    return (
      <span
        className={styles.timelineMarker}
        style={{ backgroundColor: color }}
      >
        <i className={ACTION_TYPE_ICONS[item.action_type] ?? "pi pi-circle"} />
      </span>
    );
  };

  const contentTemplate = (item: ReportActionLogEntryResponse) => {
    const meta = formatMetadata(item.metadata);
    const userName = findUserName(item.user_id);
    return (
      <div className={styles.timelineCard}>
        <div className={styles.timelineCardHeader}>
          <Tag
            value={formatLabel(item.action_type)}
            severity={ACTION_TYPE_SEVERITY[item.action_type]}
            icon={ACTION_TYPE_ICONS[item.action_type]}
          />
          <div className={styles.timelineCardMeta}>
            {userName && (
              <PantheonAvatarWithoutMenu label={userName} size="small" />
            )}
            <span className={styles.timelineCardTimestamp}>
              {formatTimestamp(item.timestamp)}
            </span>
          </div>
        </div>
        {meta && <div className={styles.timelineCardDetails}>{meta}</div>}
      </div>
    );
  };

  const cardTitle = companyName
    ? `${companyName} — Report Timeline`
    : emailSubject
      ? `${emailSubject} — Email Timeline`
      : "Event Timeline";

  return (
    <div className={sharedStyles.page}>
      <Card title={cardTitle}>
        {isLoading ? (
          <div className={sharedStyles.centered}>
            <ProgressSpinner />
          </div>
        ) : actions.length === 0 ? (
          <div className={sharedStyles.emptyState}>
            No events found for this report.
          </div>
        ) : (
          <div className={styles.timelineScrollContainer}>
            <Timeline
              value={[...actions].reverse()}
              marker={markerTemplate}
              content={contentTemplate}
              align="right"
              className={styles.timeline}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
